<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import type { PropType } from "vue";

const props = defineProps({
  secondsBeforeTimeout: {
    type: Number,
    default: 60 * 5,
  },
  events: {
    type: Array as PropType<string[]>,
    default: (): string[] => ["mousemove", "keypress"],
  },
  repeatTimer: {
    type: Boolean,
    default: false,
  },
  waitBeforeStartingSeconds: {
    type: Number,
    default: 0,
  },
  showCountdown: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: "idle"): void;
}>();

const display = ref("");
let timer: number | undefined = undefined;
let counter: number | undefined = undefined;
let start = 0;
let timeLeft = 0;
let minutes = "";
let seconds = "";

function setDisplay(): void {
  // seconds since start
  timeLeft = props.secondsBeforeTimeout - (((Date.now() - start) / 1000) | 0);
  if (timeLeft < 0 && !props.repeatTimer) {
    return;
  }
  // bitwise OR to handle parseInt
  const minute = (timeLeft / 60) | 0;
  const second = timeLeft % 60 | 0;

  minutes = `${minute < 10 ? "0" + minute : minute}`;
  seconds = `${second < 10 ? "0" + second : second}`;

  display.value = `${minutes}:${seconds}`;
}

const countdown = (): void => {
  setDisplay();
  if (timeLeft <= 0 && props.repeatTimer) {
    start = Date.now() + 1000;
  }
};

const setTimer = (): void => {
  timer = window.setInterval(idle, props.secondsBeforeTimeout * 1000);
  counter = window.setInterval(countdown, 1000);
};

const clearTimer = (): void => {
  clearInterval(timer);
  clearInterval(counter);
  setDisplay();
  start = Date.now();
  timeLeft = 0;
  setTimer();
};

const removeTimer = (): void => {
  setDisplay();
  clearInterval(timer);
  clearInterval(counter);
  for (let i = props.events.length - 1; i >= 0; i -= 1) {
    window.removeEventListener(props.events[i], clearTimer);
  }
};

const idle = (): void => {
  emit("idle");
  if (!props.repeatTimer) {
    removeTimer();
  }
};

onMounted(() => {
  setTimeout(() => {
    start = Date.now();
    setDisplay();
    nextTick(() => {
      setTimer();
      for (let i = props.events.length - 1; i >= 0; i -= 1) {
        window.addEventListener(props.events[i], clearTimer);
      }
    });
  }, props.waitBeforeStartingSeconds * 1000);
});

onBeforeUnmount(() => {
  removeTimer();
});
</script>

<template>
  <span v-if="showCountdown">{{ display }}</span>
</template>
